import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../components/markdownComponents';
import { getDocument, getDocumentSignedUrl } from '../services/documents';
import type { StashDocument } from '../types';

export function isMarkdown(doc: StashDocument) {
  if (doc.mimeType && (doc.mimeType === 'text/markdown' || doc.mimeType === 'text/x-markdown')) return true;
  return doc.name.match(/\.(md|markdown)$/i) !== null;
}

export function isImage(doc: StashDocument) {
  if (doc.mimeType && doc.mimeType.startsWith('image/')) return true;
  return doc.name.match(/\.(png|jpg|jpeg|gif|webp|svg|bmp)$/i) !== null;
}

export default function DocumentViewPage() {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<StashDocument | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!docId) return;
    getDocument(docId)
      .then(async (d: StashDocument) => {
        setDoc(d);
        const url = await getDocumentSignedUrl(d.id, 'inline');
        setFileUrl(url);
        if (isMarkdown(d)) {
          const res = await fetch(url);
          if (!res.ok) throw new Error('Failed to load file');
          setMarkdownContent(await res.text());
        }
      })
      .catch((err: unknown) => setError(String(err)))
      .finally(() => setLoading(false));
  }, [docId]);

  const handleDownload = async () => {
    if (!docId || !doc) return;
    const url = await getDocumentSignedUrl(docId, 'attachment');
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.name;
    a.click();
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Back">
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        {doc && (
          <>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 500 }}>{doc.name}</Typography>
            <Tooltip title="Download">
              <IconButton size="small" onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : error || !doc ? (
        <Typography color="error">{error || 'Document not found.'}</Typography>
      ) : isMarkdown(doc) ? (
        markdownContent === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{
            '& h1,& h2,& h3,& h4': { mt: 3, mb: 1, fontWeight: 600 },
            '& h1': { fontSize: '1.6rem' },
            '& h2': { fontSize: '1.3rem' },
            '& h3': { fontSize: '1.1rem' },
            '& p': { my: 1, lineHeight: 1.7 },
            '& ul,& ol': { pl: 3, my: 1 },
            '& li': { mb: 0.5 },
            '& code': { fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.875em' },
            '& pre': { bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', '& code': { bgcolor: 'transparent', p: 0 } },
            '& blockquote': { borderLeft: 4, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
            '& table': { borderCollapse: 'collapse', width: '100%', my: 2 },
            '& th,& td': { border: 1, borderColor: 'divider', px: 1.5, py: 0.75, textAlign: 'left' },
            '& th': { bgcolor: 'action.hover', fontWeight: 600 },
            '& hr': { my: 3, borderColor: 'divider' },
            '& a': { color: 'primary.main' },
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{markdownContent}</ReactMarkdown>
          </Box>
        )
      ) : isImage(doc) ? (
        <Box sx={{ textAlign: 'center' }}>
          {fileUrl && (
            <Box
              component="img"
              src={fileUrl}
              alt={doc.name}
              sx={{ maxWidth: '100%', height: 'auto', borderRadius: 1 }}
            />
          )}
        </Box>
      ) : null}
    </Box>
  );
}
